/* global ol */
import ExtremeMapLandmarkLayerManager from '@/lib/ExtremeMapLandmarkLayerManager'
import * as EVT from '@/consts/eventNames'
import Stream from '@/lib/Stream'

export default class ExtremeMapJointLayerManager extends ExtremeMapLandmarkLayerManager {
  constructor() {
    super()
    this.layerInfo.onJointClick = null
    this.selectedJoints = []
    this.setStream(new Stream({ name: 'jointLayerDefault', debug: false }))
  }

  getJointColorMap_() {
    const fill = {
      orange: 'rgba(238, 124, 0, 0.8)',
      blue: 'rgba(0, 122, 204, 0.8)',
      gray: 'rgba(80, 80, 80, 0.8)',
    }
    const stroke = {
      orange: 'rgba(180, 100, 0, 0.9)',
      blue: 'rgba(0, 61, 102, 0.9)',
      gray: 'rgba(30, 30, 30, 0.8)',
    }
    return {
      line: Object.assign({}, fill),
      point: {
        fill: Object.assign({}, fill),
        stroke: Object.assign({}, stroke),
      }
    }
  }

  getDefaultJointColor_(joint) {
    return joint.joint_number ? 'gray' : 'blue'
  }

  getNewJointLineStringStyle_({ color, sizeType }) {
    const colorMap = this.getJointColorMap_()
    const strokeColor = colorMap.line[color]
    let strokeWidth = 4
    if (sizeType === 'big') { strokeWidth = 9 }
    return new ol.style.Style({
      stroke: new ol.style.Stroke({
        color: strokeColor,
        width: strokeWidth
      }),
    })
  }

  getNewJointPointStyle_({ color, sizeType }) {
    const colorMap = this.getJointColorMap_()
    const fillColor = colorMap.point.fill[color]
    const strokeColor = colorMap.point.stroke[color]
    let radius = 3
    if (sizeType === 'big') { radius = 8 }
    return new ol.style.Style({
      image: new ol.style.Circle({
        radius: radius,
        fill: new ol.style.Fill({
          color: fillColor,
        }),
        stroke: new ol.style.Stroke({
          color: strokeColor,
          width: 1
        })
      }),
    })
  }

  setJointStyle_(jointId, { selected }) {
    const { ls, p1, p2 } = this.getJointFeaturesByJointId_(jointId)
    if (!ls) { return }
    {
      const color = selected ? 'orange' : ls.get('defaultColor')
      const sizeType = selected ? 'big' : ''
      const currentStyle = ls.getStyle()
      const newStyle = this.getNewJointLineStringStyle_({ color, sizeType })
      newStyle.setZIndex(currentStyle.getZIndex())
      ls.setStyle(newStyle)
    }
    [p1, p2].forEach(feat => {
      const color = selected ? 'orange' : feat.get('defaultColor')
      const sizeType = selected ? 'big' : ''
      const currentStyle = feat.getStyle()
      const newStyle = this.getNewJointPointStyle_({ color, sizeType })
      newStyle.setZIndex(currentStyle.getZIndex())
      feat.setStyle(newStyle)
    })
  }

  setJointDefaultStyle_(jointId) {
    this.setJointStyle_(jointId, { selected: false })
  }

  setJointSelectedStyle_(jointId) {
    this.setJointStyle_(jointId, { selected: true })
  }

  updateJointDefaultStyle_(joint) {
    const jointId = joint.id
    const color = this.getDefaultJointColor_(joint)
    const { ls, p1, p2 } = this.getJointFeaturesByJointId_(jointId)
    ls.set('defaultColor', color)
    p1.set('defaultColor', color)
    p2.set('defaultColor', color)
  }

  getNewJointLineStringFeature_(points, info) {
    const feat = new ol.Feature({
      geometry: new ol.geom.LineString(points)
    })
    feat.setId(info.id)
    feat.set('defaultColor', info.defaultColor)
    const style = this.getNewJointLineStringStyle_({
      color: info.defaultColor,
    })
    style.setZIndex(info.zIndex++)
    feat.setStyle(style)
    return feat
  }

  getNewJointPointFeature_(point, info) {
    const feat = new ol.Feature({
      geometry: new ol.geom.Point(point)
    })
    feat.setId(info.id)
    feat.set('defaultColor', info.defaultColor)
    const style = this.getNewJointPointStyle_({
      color: info.defaultColor,
    })
    style.setZIndex(info.zIndex++)
    feat.setStyle(style)
    return feat
  }

  getJointFeaturesByJointId_(jointId) {
    const src = this.layer.getSource()
    const ls = src.getFeatureById(`${jointId}-ls`)
    const p1 = src.getFeatureById(`${jointId}-p1`)
    const p2 = src.getFeatureById(`${jointId}-p2`)
    return { ls, p1, p2 }
  }

  createJointLayer_(srcArr) {
    const info = {
      id: '',
      zIndex: 0,
      defaultColor: 'blue',
    }
    const coords = []
    const feats = []
    for (const e of srcArr) {
      info.defaultColor = this.getDefaultJointColor_(e)
      const p1 = this.coordFromLonLat(e.line.coordinates[0])
      const p2 = this.coordFromLonLat(e.line.coordinates[1])
      coords.push(p1, p2)
      info.id = `${e.id}-ls`
      feats.push(this.getNewJointLineStringFeature_([p1, p2], info))
      info.id = `${e.id}-p1`
      feats.push(this.getNewJointPointFeature_(p1, info))
      info.id = `${e.id}-p2`
      feats.push(this.getNewJointPointFeature_(p2, info))
    }

    const layer = new ol.layer.Vector({
      source: new ol.source.Vector({ features: feats }),
    })
    layer.name = 'joint'
    layer.setZIndex = 500

    let extent = null
    if (coords.length > 0) {
      extent = ol.extent.boundingExtent(coords)
    }

    this.layer = layer
    this.layerInfo.extent = extent

    this.layerInfo.onJointClick = ({ event, feature }) => {
      // 重なってたりする場合はそれぞれ飛んでくるので、一回で止める
      if (event.originalEvent.defaultPrevented) { return }
      event.preventDefault()
      const [jointId] = feature.getId().split('-')
      this.selectJoints([parseInt(jointId)], { emitEvent: true })
    }
  }

  prepareLayer(srcArr) {
    this.createJointLayer_(srcArr)
    // 選択を空にする
    this.selectedJoints = []

    return this.getLayer()
  }

  setListeners_() {
    this.stream.recv(EVT.SELECT_JOINTS,
      ({ ids }) => {
        if (!ids || ids.length === 0) { return }
        this.selectJoints(ids, { emitEvent: false })
      }
    )
    this.stream.recv(EVT.UNSELECT_JOINTS,
      ({ ids }) => {
        if (!ids || ids.length === 0) { return }
        this.unselectJoints(ids)
      }
    )
    this.stream.recv(EVT.UPDATED_JOINTS,
      ({ joints }) => {
        if (!joints || joints.length === 0) { return }
        joints.forEach(joint => {
          this.updateJointDefaultStyle_(joint)
        })
      }
    )
  }

  unselectJoints(ids) {
    ids.forEach(e => {
      this.setJointDefaultStyle_(e)
    })
    this.selectedJoints = this.selectedJoints.filter(id => {
      return ids.indexOf(id) === -1
    })
  }

  selectJoints(ids, { emitEvent }) {
    const currentSelectedJoints = this.selectedJoints
    this.unselectJoints(this.selectedJoints)

    // 全部同じだったら再selectしない
    if (this.isArrayAllSame(currentSelectedJoints, ids)) {
      if (emitEvent) {
        this.stream.send(EVT.UNSELECT_JOINTS, { ids: ids })
      }
      return
    }

    this.selectedJoints = ids
    this.selectedJoints.forEach(e => {
      this.setJointSelectedStyle_(e)
    })
    if (emitEvent) {
      this.stream.send(EVT.SELECT_JOINTS, { ids: this.selectedJoints })
    }
  }
}
